import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { sha256 } from "js-sha256";
import { useAuth, API_URL } from "../App";

const leadingZeroBits = (hex) => {
	let bits = 0;
	for (const ch of hex) {
		const v = parseInt(ch, 16);
		if (v === 0) {
			bits += 4;
			continue;
		}
		return bits + Math.clz32(v) - 28;
	}
	return bits;
};

// Solves the proof-of-work puzzle in small slices so the page stays responsive
const solvePuzzle = (salt, bits, cancelled) =>
	new Promise((resolve) => {
		let counter = 0;
		const work = () => {
			if (cancelled()) return;
			const end = counter + 2000;
			for (; counter < end; counter++) {
				if (leadingZeroBits(sha256(`${salt}:${counter}`)) >= bits) {
					resolve(counter);
					return;
				}
			}
			setTimeout(work, 0);
		};
		work();
	});

function Login() {
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [website, setWebsite] = useState(""); // honeypot: left empty by real users
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [botCheck, setBotCheck] = useState(null);
	const [slowStart, setSlowStart] = useState(false);
	const { login } = useAuth();
	const alive = useRef(true);

	// A sleeping free-tier server can take a minute to answer the first request
	useEffect(() => {
		if (botCheck) {
			setSlowStart(false);
			return undefined;
		}
		const timer = setTimeout(() => setSlowStart(true), 4000);
		return () => clearTimeout(timer);
	}, [botCheck]);

	const prepareBotCheck = useCallback(async () => {
		setBotCheck(null);
		try {
			const { data } = await axios.get(`${API_URL}/auth/challenge`);
			const counter = await solvePuzzle(
				data.salt,
				data.bits,
				() => !alive.current,
			);
			if (alive.current)
				setBotCheck({ powToken: data.token, powCounter: counter });
		} catch (err) {
			if (alive.current)
				setError(
					err.response?.data?.error ||
						"Could not verify the browser. Please reload the page.",
				);
		}
	}, []);

	useEffect(() => {
		alive.current = true;
		prepareBotCheck();
		return () => {
			alive.current = false;
		};
	}, [prepareBotCheck]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!botCheck) return;
		setError("");
		setLoading(true);

		try {
			await login(username, password, { ...botCheck, website });
		} catch (err) {
			setError(
				err.response?.data?.error || "Login failed. Please try again.",
			);
			// Each puzzle works once, so get a fresh one for the next try
			prepareBotCheck();
		} finally {
			setLoading(false);
		}
	};

	return (
		<div
			style={{
				minHeight: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				background: "linear-gradient(135deg, #1e3a5f 0%, #0e7490 100%)",
			}}
		>
			<div
				style={{
					background: "white",
					padding: "2rem",
					borderRadius: "1rem",
					boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
					width: "100%",
					maxWidth: "400px",
				}}
			>
				<div style={{ textAlign: "center", marginBottom: "2rem" }}>
					<h1
						style={{
							color: "#0891b2",
							fontSize: "1.75rem",
							marginBottom: "0.5rem",
						}}
					>
						Lab Scheduler
					</h1>
					<p style={{ color: "#64748b", fontSize: "0.875rem" }}>
						Sign in to your account
					</p>
				</div>

				{error && <div className='alert alert-error'>{error}</div>}

				<form onSubmit={handleSubmit}>
					{/* Honeypot: hidden from people and screen readers, bots tend to fill it */}
					<input
						type='text'
						name='website'
						value={website}
						onChange={(e) => setWebsite(e.target.value)}
						tabIndex={-1}
						autoComplete='off'
						aria-hidden='true'
						style={{
							position: "absolute",
							left: "-10000px",
							width: "1px",
							height: "1px",
							opacity: 0,
						}}
					/>
					<div className='form-group'>
						<label className='form-label' htmlFor='username'>
							Username
						</label>
						<input
							id='username'
							type='text'
							className='form-input'
							value={username}
							onChange={(e) => setUsername(e.target.value)}
							placeholder='Enter your username'
							required
						/>
					</div>

					<div className='form-group'>
						<label className='form-label' htmlFor='password'>
							Password
						</label>
						<input
							id='password'
							type='password'
							className='form-input'
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							placeholder='Enter your password'
							required
						/>
					</div>

					<button
						type='submit'
						className='btn btn-primary btn-lg'
						style={{ width: "100%", marginTop: "1rem" }}
						disabled={loading || !botCheck}
					>
						{loading
							? "Signing in..."
							: botCheck
								? "Sign In"
								: "Verifying..."}
					</button>
				</form>

				{slowStart && !error && (
					<p
						style={{
							marginTop: "1rem",
							fontSize: "0.8rem",
							color: "#64748b",
							textAlign: "center",
						}}
					>
						Waking up the server, this can take up to a minute...
					</p>
				)}
			</div>
		</div>
	);
}

export default Login;
